import type { Account, Snapshot } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { FEATURES_SUPPORT_VERSIONS } from '@/configs/featureConfig';
import { useEnvironment } from '@/contexts/useEnvironment';

import { ManagedDatabaseConfiguration } from './managedDatabaseConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { GcpNetwork } from '../selectors/gcpNetworkSelect';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreGcpCloudSqlState {
  resourceId: string;
  snapshot: Snapshot;
  resourceName: string;
  network?: GcpNetwork;
  zone?: string;
  supportedCloudProviders: Provider[];
  supportedInstalledVersion?: string;
  restoreAccount?: Account;
  regionName?: string;
  initialTags: Record<string, string>;
  keepOriginalTags: boolean;
  tags: Record<string, string>;
}

const restoreGcpCloudSqlFlow: Step<RestoreGcpCloudSqlState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'gcp_cloud_sql_configuration',
      Component: ManagedDatabaseConfiguration,
    },
  ],
};

export const RestoreGcpCloudSqlWizard = ({
  snapshot,
  onClose,
  onBack,
}: {
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { isDemo, isDev } = useEnvironment();

  return (
    <Wizard<RestoreGcpCloudSqlState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreGcpCloudSqlFlow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders:
          isDemo || isDev
            ? [Provider.Aws, Provider.Azure, Provider.Gcp]
            : [Provider.Gcp],
        supportedInstalledVersion:
          FEATURES_SUPPORT_VERSIONS.GCP_RESTORE_ACCOUNT_RESTORE_ACCOUNT,
        keepOriginalTags: true,
        resourceName: `${
          snapshot.resource?.properties?.gcpCloudSql?.instanceId ?? ''
        }-restored`,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        regionName:
          snapshot.resource?.properties?.gcpCloudSql?.region || 'us-east1',
        zone: snapshot.resource?.properties?.gcpCloudSql?.zone,
      }}
      title={`Restore Cloud SQL`}
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
